import { useEffect, useState } from 'react';
import { usersApi } from '../../lib/api';
import type { AppUser } from '../../lib/api';
import { useMobile } from '../../hooks/useMobile';
import { apiErrorMessage } from '../../lib/apiError';
import { roleStyle } from '../../lib/roleStyles';

const ROLES = ['admin', 'member', 'watcher'] as const;

const emptyForm: { name: string; phone: string; role: 'admin' | 'member' | 'watcher' } = { name: '', phone: '', role: 'member' };

export default function UsersTab() {
  const isMobile = useMobile();
  const [users, setUsers]           = useState<AppUser[]>([]);
  const [loading, setLoading]       = useState(true);
  const [showAdd, setShowAdd]       = useState(false);
  const [form, setForm]             = useState(emptyForm);
  const [formError, setFormError]   = useState('');
  const [formBusy, setFormBusy]     = useState(false);
  const [actionBusy, setActionBusy] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const { data } = await usersApi.list();
      setUsers(data);
    } finally {
      setLoading(false);
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');
    if (!form.name.trim() || !form.phone.trim()) {
      setFormError('Name and phone number are required.');
      return;
    }
    setFormBusy(true);
    try {
      await usersApi.create({
        name: form.name.trim(),
        phone: form.phone.trim(),
        role: form.role,
      });
      setForm(emptyForm);
      setShowAdd(false);
      await load();
    } catch (err: unknown) {
      setFormError(apiErrorMessage(err, 'Failed to invite user.'));
    } finally {
      setFormBusy(false);
    }
  }

  async function toggleActive(user: AppUser) {
    setActionBusy(user.id);
    try {
      await usersApi.update(user.id, { is_active: !user.is_active });
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, is_active: !u.is_active } : u));
    } finally {
      setActionBusy(null);
    }
  }

  async function changeRole(user: AppUser, role: string) {
    setActionBusy(user.id);
    try {
      await usersApi.update(user.id, { role: role as AppUser['role'] });
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, role: role as AppUser['role'] } : u));
    } finally {
      setActionBusy(null);
    }
  }

  async function resendInvite(user: AppUser) {
    setActionBusy(user.id);
    try {
      await usersApi.resendInvite(user.id);
    } finally {
      setActionBusy(null);
    }
  }

  if (loading) return (
    <div style={{ padding: 32, textAlign: 'center', color: 'var(--shell-xs)', fontSize: '0.82rem' }}>
      Loading users…
    </div>
  );

  return (
    <div style={{ padding: isMobile ? '12px 12px' : '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--shell-xs)' }}>
          {users.length} user{users.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={() => { setShowAdd((v) => !v); setFormError(''); setForm(emptyForm); }}
          style={{
            background: showAdd ? 'rgba(226,75,74,.18)' : 'rgba(29,158,117,.2)',
            color: showAdd ? 'var(--red)' : 'var(--v2)',
            border: 'none', borderRadius: 4, padding: '7px 14px',
            fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
          }}
        >
          {showAdd ? '✕ Cancel' : '+ Invite User'}
        </button>
      </div>

      {/* Invite form */}
      {showAdd && (
        <form
          onSubmit={handleInvite}
          style={{
            background: 'rgba(29,158,117,.07)', border: '1px solid rgba(29,158,117,.2)',
            borderRadius: 6, padding: isMobile ? '14px 14px' : '18px 20px',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}
        >
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--shell-muted)', marginBottom: 2 }}>
            Invite Staff Member
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--shell-xs)' }}>
            They'll receive a WhatsApp message with a link to set up their password.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
            <Field label="Full Name *">
              <input
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Smith" autoFocus style={inputStyle}
              />
            </Field>
            <Field label="WhatsApp Number *">
              <input
                type="tel"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="27821234567" style={inputStyle}
              />
            </Field>
          </div>

          <Field label="Role">
            <div style={{ display: 'flex', gap: 8 }}>
              {ROLES.map((r) => (
                <button
                  key={r} type="button"
                  onClick={() => setForm({ ...form, role: r })}
                  style={{
                    padding: '5px 12px', borderRadius: 3, border: 'none', cursor: 'pointer',
                    fontSize: '0.78rem', fontWeight: 600, transition: 'all .12s',
                    background: form.role === r ? roleStyle(r).background : 'var(--hover-strong)',
                    color: form.role === r ? roleStyle(r).color : 'var(--shell-xs)',
                    outline: form.role === r ? `1px solid ${roleStyle(r).color}` : 'none',
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
          </Field>

          {formError && (
            <div style={{ fontSize: '0.78rem', color: 'var(--red)', background: 'rgba(226,75,74,.1)', padding: '8px 10px', borderRadius: 4 }}>
              {formError}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="submit" disabled={formBusy}
              style={{
                background: 'var(--v1)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 4,
                padding: '8px 20px', fontSize: '0.82rem', fontWeight: 600,
                cursor: formBusy ? 'not-allowed' : 'pointer', opacity: formBusy ? 0.6 : 1,
              }}
            >
              {formBusy ? 'Sending invite…' : 'Send Invite'}
            </button>
          </div>
        </form>
      )}

      {/* User list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {users.map((user) => (
          <UserRow
            key={user.id}
            user={user}
            isMobile={isMobile}
            busy={actionBusy === user.id}
            onToggleActive={() => toggleActive(user)}
            onRoleChange={(role) => changeRole(user, role)}
            onResendInvite={() => resendInvite(user)}
          />
        ))}
      </div>
    </div>
  );
}

function UserRow({ user, isMobile, busy, onToggleActive, onRoleChange, onResendInvite }: {
  user: AppUser;
  isMobile: boolean;
  busy: boolean;
  onToggleActive: () => void;
  onRoleChange: (role: string) => void;
  onResendInvite: () => void;
}) {
  const initials = user.avatar_initials ?? user.avatarInitials
    ?? user.name.split(' ').map((p) => p[0]).join('').toUpperCase().slice(0, 2);
  const isInvited = user.account_setup === false;

  return (
    <div style={{
      display: 'flex', alignItems: isMobile ? 'flex-start' : 'center',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? 8 : 12, padding: isMobile ? '12px 14px' : '10px 14px',
      background: user.is_active === false ? 'rgba(255,255,255,.02)' : 'rgba(255,255,255,.04)',
      borderRadius: 5, border: '1px solid var(--shell-border)',
      opacity: user.is_active === false ? 0.55 : 1,
    }}>
      {/* Avatar + info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
        <div style={{
          width: 34, height: 34, borderRadius: '50%',
          background: isInvited ? 'var(--hover-strong)' : 'var(--vs2)',
          color: isInvited ? 'var(--shell-xs)' : 'var(--teal-300)',
          border: isInvited ? '1px dashed var(--shell-border)' : 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
        }}>
          {initials}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.83rem', fontWeight: 600, color: 'var(--shell-text)', display: 'flex', alignItems: 'center', gap: 7 }}>
            {user.name}
            {isInvited && (
              <span style={{ fontSize: '0.66rem', color: 'var(--amber, #f59e0b)', background: 'rgba(245,158,11,.12)', padding: '1px 6px', borderRadius: 3 }}>
                invited
              </span>
            )}
            {user.is_active === false && (
              <span style={{ fontSize: '0.68rem', color: 'var(--shell-xs)', fontWeight: 400 }}>inactive</span>
            )}
          </div>
          <div style={{ fontSize: '0.73rem', color: 'var(--shell-xs)', marginTop: 1 }}>
            +{user.phone}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: isMobile ? 44 : 0 }}>
        {isInvited && (
          <button
            onClick={onResendInvite}
            disabled={busy}
            style={{
              background: 'rgba(245,158,11,.12)', color: 'var(--amber, #f59e0b)',
              border: 'none', borderRadius: 3, padding: '4px 10px',
              fontSize: '0.72rem', fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.5 : 1, whiteSpace: 'nowrap',
            }}
          >
            {busy ? '…' : 'Resend'}
          </button>
        )}

        <select
          value={user.role}
          onChange={(e) => onRoleChange(e.target.value)}
          disabled={busy}
          style={{
            background: roleStyle(user.role).background,
            color: roleStyle(user.role).color,
            border: 'none', borderRadius: 3, padding: '4px 8px',
            fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
            appearance: 'none', textAlign: 'center', minWidth: 72,
          }}
        >
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>

        <button
          onClick={onToggleActive}
          disabled={busy}
          style={{
            background: user.is_active === false ? 'rgba(29,158,117,.15)' : 'rgba(226,75,74,.12)',
            color: user.is_active === false ? 'var(--v2)' : 'var(--red)',
            border: 'none', borderRadius: 3, padding: '4px 10px',
            fontSize: '0.75rem', fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy ? 0.5 : 1, whiteSpace: 'nowrap',
          }}
        >
          {busy ? '…' : user.is_active === false ? 'Reactivate' : 'Deactivate'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--shell-xs)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: 'var(--vs2)', border: '1px solid var(--shell-border)',
  borderRadius: 4, padding: '8px 10px', color: 'var(--shell-text)',
  fontSize: '0.82rem', width: '100%', outline: 'none',
};
